import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { getOrder, simulatePayment, cancelOrder } from '../services/api';

function formatRupiah(n) { return 'Rp ' + n.toLocaleString('id-ID'); }
function pad(n) { return n.toString().padStart(2,'0'); }
function formatTime(s) { return `${pad(Math.floor(s/60))}:${pad(s%60)}`; }

const DURATION = 600;

export default function PaymentPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(location.state || null);
  const [countdown, setCountdown] = useState(DURATION);
  const [expired, setExpired] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const startRef = useRef(Date.now());

  const fetchOrder = useCallback(async () => {
    try {
      const res = await getOrder(orderId);
      const d = res.data.data;
      setOrder(d);
      if (d.status === 'paid' || d.status === 'completed') {
        clearInterval(timerRef.current); clearInterval(pollRef.current);
        navigate(`/order/${orderId}`);
      }
      if (d.status === 'cancelled') {
        clearInterval(timerRef.current); clearInterval(pollRef.current);
        setExpired(true);
      }
    } catch(e) { console.error(e); }
  }, [orderId, navigate]);

  useEffect(() => { if (!order) fetchOrder(); }, []);

  useEffect(() => {
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const rem = DURATION - Math.floor((Date.now() - startRef.current) / 1000);
      if (rem <= 0) { setExpired(true); setCountdown(0); clearInterval(timerRef.current); }
      else setCountdown(rem);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    pollRef.current = setInterval(fetchOrder, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchOrder]);

  const handleSimulate = async () => {
    setSimLoading(true);
    try { await simulatePayment(orderId); } catch(e) {}
    setSimLoading(false);
  };

  const handleCancel = async () => {
    setCancelLoading(true); setCancelError('');
    try {
      await cancelOrder(orderId);
      clearInterval(timerRef.current); clearInterval(pollRef.current);
      navigate('/', { replace: true });
    } catch(e) {
      setCancelError(e.response?.data?.error || 'Gagal membatalkan.');
      setCancelLoading(false);
    }
  };

  const qris = order?.qrisString || order?.payment?.qrisString;
  const total = order?.totalPayment || order?.payment?.totalPayment;
  const fee = order?.fee || order?.payment?.fee;
  const urgent = countdown <= 60 && countdown > 0;
  const pct = (countdown / DURATION) * 100;

  if (!order) return <div className="loading"><div className="spinner"></div>Memuat...</div>;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }} className="page-enter">
      <div className="page-title">Pembayaran QRIS</div>

      <div className="section-box">
        <div className="info-box">
          <div className="info-box-item">
            <div className="label">Order ID</div>
            <div className="val">{orderId}</div>
          </div>
          <div className="info-box-item" style={{ textAlign: 'right' }}>
            <div className="label">Pembeli</div>
            <div className="val">{order?.customer?.name}</div>
          </div>
        </div>

        {expired ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>⏰</div>
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              <strong>QRIS Kadaluarsa</strong><br />
              <span style={{ fontSize: '0.83rem' }}>Waktu 10 menit habis. Stok produk sudah dikembalikan.</span>
            </div>
            <button className="btn btn-primary btn-full" onClick={() => navigate('/')}>🛍️ Pesan Ulang</button>
          </div>
        ) : (
          <div className="qris-center">
            <div className="qris-label">Total Pembayaran</div>
            <div className="qris-amount">{formatRupiah(total || 0)}</div>

            {qris && (
              <div className="qris-frame">
                <QRCodeSVG value={qris} size={210} level="M" />
              </div>
            )}

            {fee > 0 && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text3)', marginBottom: 14, fontFamily: 'var(--mono)' }}>
                {formatRupiah((total||0)-fee)} + biaya layanan {formatRupiah(fee)}
              </div>
            )}

            {/* Progress bar timer */}
            <div style={{ width: '100%', height: 4, background: 'var(--bg3)', borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2,
                width: `${pct}%`,
                background: urgent ? 'var(--danger)' : 'var(--primary)',
                transition: 'width 1s linear, background 0.5s',
                boxShadow: `0 0 8px ${urgent ? 'var(--danger)' : 'var(--primary)'}`,
              }} />
            </div>

            <div className={`qris-timer ${urgent ? 'urgent' : ''}`}>
              <div className="dot" />
              <span>Berlaku {formatTime(countdown)}</span>
            </div>

            <div className="steps" style={{ marginTop: 20, textAlign: 'left' }}>
              <div className="step"><div className="step-num">1</div><div className="step-text">Buka aplikasi e-wallet atau mobile banking</div></div>
              <div className="step"><div className="step-num">2</div><div className="step-text">Pilih menu <strong>Scan QR</strong> atau <strong>QRIS</strong></div></div>
              <div className="step"><div className="step-num">3</div><div className="step-text">Arahkan kamera ke QR Code di atas</div></div>
              <div className="step"><div className="step-num">4</div><div className="step-text">Konfirmasi nominal dan selesaikan pembayaran</div></div>
            </div>

            <div style={{ marginTop: 14, fontSize: '0.75rem', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
              ⚡ Status terupdate otomatis setiap 5 detik
            </div>
          </div>
        )}
      </div>

      {/* Batalkan Pesanan */}
      {!expired && (
        <div className="cancel-box">
          {!showConfirm ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>Ingin membatalkan?</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>Stok akan dikembalikan otomatis</div>
              </div>
              <button className="btn btn-danger" onClick={() => setShowConfirm(true)}>Batalkan</button>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: '0.92rem' }}>⚠️ Yakin ingin membatalkan?</div>
              <div style={{ fontSize: '0.83rem', color: 'var(--text2)', marginBottom: 14 }}>
                Pesanan <span style={{ fontFamily: 'var(--mono)', color: 'var(--text)' }}>{orderId}</span> akan dibatalkan.
              </div>
              {cancelError && <div className="alert alert-error">{cancelError}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-danger" style={{ flex: 1 }} onClick={handleCancel} disabled={cancelLoading}>
                  {cancelLoading ? '⏳...' : '✅ Ya, Batalkan'}
                </button>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowConfirm(false)} disabled={cancelLoading}>
                  Tidak
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {process.env.NODE_ENV !== 'production' && !expired && (
        <div className="sandbox">
          <div className="sandbox-title">⚗️ Sandbox / Development</div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 12 }}>Simulasikan pembayaran tanpa scan QR.</p>
          <button className="btn btn-success btn-full" onClick={handleSimulate} disabled={simLoading}>
            {simLoading ? '⏳ Memproses...' : '✅ Simulasi Bayar'}
          </button>
        </div>
      )}
    </div>
  );
}
